import React, { useReducer, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { api, unwrapEnvelope } from "@/lib/api";
import {
  SYNC_OPTIONS,
  initialWalmartConnectState,
  walmartConnectReducer,
} from "./walmartConnectReducer";

export function WalmartConnectModal({ open, onOpenChange, onConnected }) {
  const [state, dispatch] = useReducer(
    walmartConnectReducer,
    initialWalmartConnectState
  );

  useEffect(() => {
    if (!open) {
      dispatch({ type: "RESET" });
    }
  }, [open]);

  const visibleSync = SYNC_OPTIONS.filter(
    (opt) => state.capabilities?.[opt.id]
  );

  async function handleVerify() {
    dispatch({ type: "VERIFY_START" });
    try {
      const res = await api.walmartVerify({
        client_id: state.credentials.clientId.trim(),
        client_secret: state.credentials.clientSecret,
      });
      const data = unwrapEnvelope(res);
      dispatch({
        type: "VERIFY_SUCCESS",
        seller: data.seller,
        capabilities: data.capabilities,
      });
    } catch (err) {
      dispatch({
        type: "VERIFY_ERROR",
        message:
          err?.response?.data?.detail ||
          "Unable to verify Walmart credentials.",
      });
    }
  }

  async function handleConnect() {
    dispatch({ type: "CONNECT_START" });
    try {
      await api.walmartConnect({
        client_id: state.credentials.clientId.trim(),
        client_secret: state.credentials.clientSecret,
        sync_settings: state.syncSettings,
      });
      dispatch({ type: "CONNECT_SUCCESS" });
      onConnected?.();
      onOpenChange(false);
    } catch (err) {
      dispatch({
        type: "CONNECT_ERROR",
        message:
          err?.response?.data?.detail ||
          "Unable to connect Walmart Seller Central.",
      });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[90]" />
        <Dialog.Content
          className="integration-modal fixed left-1/2 top-1/2 z-[91] w-[min(900px,calc(100vw-24px))] max-h-[90dvh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 bg-white rounded-panel border border-edge-subtle shadow-menu p-5"
          data-testid="walmart-connect-modal"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <Dialog.Title className="text-title text-ink-primary">
                Connect Walmart Seller Central
              </Dialog.Title>
              <Dialog.Description className="text-compact text-ink-muted mt-1">
                Connect your Walmart Marketplace API credentials to securely access
                your seller account.
              </Dialog.Description>
            </div>
            <Dialog.Close className="p-1 rounded-ctrl hover:bg-surface-2">
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="flex gap-2 text-micro font-mono uppercase mb-4">
            {["credentials", "verify", "sync"].map((s, i) => (
              <span
                key={s}
                className={
                  state.step === s || (s === "verify" && state.step === "sync")
                    ? "text-emerald-600"
                    : "text-ink-muted"
                }
              >
                {i + 1}. {s}
              </span>
            ))}
          </div>

          {state.step === "credentials" && (
            <div className="space-y-4">
              <label className="block text-compact">
                <span className="text-ink-secondary">Client ID</span>
                <input
                  autoComplete="off"
                  className="mt-1 w-full h-9 px-3 rounded-ctrl border border-edge-subtle"
                  value={state.credentials.clientId}
                  placeholder="Enter Walmart Client ID"
                  onChange={(e) =>
                    dispatch({
                      type: "SET_FIELD",
                      field: "clientId",
                      value: e.target.value,
                    })
                  }
                />
                <span className="text-micro text-ink-muted mt-1 block">
                  Found in your Walmart Seller Center API credentials.
                </span>
              </label>

              <label className="block text-compact">
                <span className="text-ink-secondary">Client Secret</span>
                <SecretInput
                  value={state.credentials.clientSecret}
                  onChange={(value) =>
                    dispatch({
                      type: "SET_FIELD",
                      field: "clientSecret",
                      value,
                    })
                  }
                />
              </label>

              {state.verificationError && (
                <p className="text-compact text-red-600" data-testid="walmart-verify-error">
                  {state.verificationError}
                </p>
              )}

              <div className="integration-modal__footer flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="h-9 px-3 rounded-ctrl bg-signal-blue text-white text-compact disabled:opacity-50"
                  disabled={
                    state.verifying ||
                    !state.credentials.clientId.trim() ||
                    !state.credentials.clientSecret
                  }
                  onClick={handleVerify}
                >
                  {state.verifying ? "Testing connection…" : "Test connection →"}
                </button>
              </div>
            </div>
          )}

          {state.step === "verify" && state.seller && (
            <div className="space-y-4">
              <PanelCopy
                title="Walmart Seller Account"
                body="We found this Walmart seller account. Confirm this is the account you want to connect."
              />
              <dl className="grid grid-cols-2 gap-3 text-compact">
                <Field label="Seller" value={state.seller.seller_name} />
                <Field label="Seller ID" value={state.seller.seller_id} />
                <Field label="Marketplace" value={state.seller.marketplace} />
                <Field label="Account status" value={state.seller.account_status} />
              </dl>
              <span className="inline-flex text-micro font-mono uppercase px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                Verified
              </span>
              <div className="integration-modal__footer flex justify-between gap-2 pt-2">
                <button
                  type="button"
                  className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact"
                  onClick={() => dispatch({ type: "SET_STEP", step: "credentials" })}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="h-9 px-3 rounded-ctrl bg-signal-blue text-white text-compact"
                  onClick={() => dispatch({ type: "SET_STEP", step: "sync" })}
                >
                  Confirm account →
                </button>
              </div>
            </div>
          )}

          {state.step === "sync" && (
            <div className="space-y-4">
              <PanelCopy
                title="Sync settings"
                body="Choose which Walmart data Ezisell should sync for this workspace."
              />
              <div className="space-y-2">
                {visibleSync.map((opt) => (
                  <label
                    key={opt.id}
                    className="flex items-center justify-between py-1 text-compact"
                  >
                    <span>{opt.title}</span>
                    <input
                      type="checkbox"
                      checked={!!state.syncSettings[opt.id]}
                      onChange={(e) =>
                        dispatch({
                          type: "TOGGLE_SYNC",
                          id: opt.id,
                          value: e.target.checked,
                        })
                      }
                    />
                  </label>
                ))}
              </div>
              {state.connectError && (
                <p className="text-compact text-red-600">{state.connectError}</p>
              )}
              <div className="integration-modal__footer flex justify-between gap-2 pt-2">
                <button
                  type="button"
                  className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact"
                  onClick={() => dispatch({ type: "SET_STEP", step: "verify" })}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="h-9 px-3 rounded-ctrl bg-emerald-600 text-white text-compact disabled:opacity-50"
                  disabled={state.connecting}
                  onClick={handleConnect}
                >
                  {state.connecting ? "Connecting…" : "Connect Walmart"}
                </button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SecretInput({ value, onChange }) {
  const [show, setShow] = React.useState(false);
  return (
    <div className="secret-input mt-1">
      <input
        type={show ? "text" : "password"}
        autoComplete="new-password"
        className="w-full h-9 px-3 rounded-ctrl border border-edge-subtle"
        value={value}
        placeholder="Enter Walmart Client Secret"
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="h-9 px-2 text-compact text-ink-muted"
        onClick={() => setShow((c) => !c)}
      >
        {show ? "Hide" : "Show"}
      </button>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-micro text-ink-muted uppercase">{label}</dt>
      <dd className="text-ink-primary">{value || "—"}</dd>
    </div>
  );
}

function PanelCopy({ title, body }) {
  return (
    <div>
      <h4 className="text-stitle text-ink-primary">{title}</h4>
      <p className="text-compact text-ink-secondary mt-1">{body}</p>
    </div>
  );
}
